import React, { FC, ReactNode, useState } from "react";
import classNames from "classnames";
import { Box } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { IconRefreshGlyph } from "../panels";

/**
 * The panel-local state block — four skins on shared bones (handoff §11, step 1).
 *
 * Built here rather than by widening the shared `PanelEmpty`. Every state on
 * this panel needs something that block cannot express: a primary button, a
 * helper line under it, an inline confirm that replaces the block, a mono error
 * block, a progress sweep. Widening PanelEmpty for that would turn a
 * presentational block into a small state machine, and its other consumers on
 * this page must stay copy-only. Same bones — 40px icon plate, 13.5px Black
 * headline, 12px body, min-height 176 — so the vocabulary is shared even though
 * the component is not.
 */

export type ScoreStateSkin = "empty" | "working" | "error" | "form";

interface Props {
  skin: ScoreStateSkin;
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  /** Buttons, rendered under the body. */
  action?: ReactNode;
  /** 11px --text-2 line directly under the action. */
  helper?: ReactNode;
  /** Anything below: a poll indicator, a leave-warning, a raw error block. */
  footer?: ReactNode;
  /** 193px hosts: 36px plate, 12.5px headline, no body max-width. */
  compact?: boolean;
  className?: string;
}

const SKIN: Record<ScoreStateSkin, { box: string; glyph: string; border: string }> = {
  empty: { box: "bg-surface-2 border-dashed", glyph: "text-text-2", border: "var(--track)" },
  working: { box: "bg-tint", glyph: "text-accent-text", border: "transparent" },
  error: { box: "bg-down-bg", glyph: "text-down", border: "transparent" },
  form: { box: "bg-surface", glyph: "", border: "var(--track)" },
};

export const ScoreState: FC<Props> = ({
  skin,
  icon,
  title,
  body,
  action,
  helper,
  footer,
  compact = false,
  className,
}) => {
  const s = SKIN[skin];
  const form = skin === "form";
  const padding = (() => {
    if (form) return undefined;
    return compact ? "18px 12px" : "22px 20px";
  })();
  return (
    <div
      className={classNames(
        "rounded-xl border flex flex-col",
        s.box,
        form ? "items-stretch text-left p-4 justify-start" : "items-center text-center justify-center",
        className,
      )}
      style={{ minHeight: 176, borderColor: s.border, padding }}
    >
      {!form && icon && (
        <span
          className={classNames("rounded-full bg-surface inline-flex items-center justify-center shrink-0", s.glyph)}
          style={{ width: compact ? 36 : 40, height: compact ? 36 : 40 }}
        >
          {icon}
        </span>
      )}
      <div
        className={classNames("font-CircularStdBlack text-text-1", !form && icon && "mt-3")}
        style={{ fontSize: compact ? 12.5 : 13.5, lineHeight: 1.25 }}
      >
        {title}
      </div>
      {body && (
        <div
          className={classNames("text-text-2", !form && !compact && "max-w-[360px]")}
          style={{ fontSize: 12, lineHeight: 1.5, marginTop: 4 }}
        >
          {body}
        </div>
      )}
      {action && (
        <div
          className={classNames("flex flex-wrap gap-2", form ? "justify-start" : "justify-center", compact && "w-full")}
          style={{ marginTop: 14 }}
        >
          {action}
        </div>
      )}
      {helper && (
        <div className="text-text-2" style={{ fontSize: 11, marginTop: 8 }}>
          {helper}
        </div>
      )}
      {footer && (
        <div className="w-full" style={{ marginTop: 12 }}>
          {footer}
        </div>
      )}
    </div>
  );
};

/** The header chip beside the panel title: Unavailable · Not current · Delayed · Queued · Scoring. */
export const StateChip: FC<{ tone: "down" | "tint"; children: ReactNode }> = ({ tone, children }) => (
  <span
    className={classNames(
      "inline-flex items-center h-[22px] px-2.5 rounded-full font-CircularStdBlack shrink-0",
      tone === "down" ? "bg-down-bg text-down" : "bg-tint text-accent-text",
    )}
    style={{ fontSize: 11 }}
  >
    {children}
  </span>
);

/** 32px outlined action — Retry, Check again, Cancel. */
export const OutlineButton: FC<{
  onClick?: () => void;
  icon?: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  full?: boolean;
}> = ({ onClick, icon, children, disabled, full }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={classNames(
      "inline-flex items-center justify-center gap-1.5 h-8 px-3.5 rounded-[10px] border bg-surface text-text-1 transition-colors hover:border-accent disabled:opacity-60 disabled:cursor-not-allowed",
      full && "w-full",
    )}
    style={{ fontSize: 12.5, borderColor: "var(--track)" }}
  >
    {icon}
    {children}
  </button>
);

export const RetryButton: FC<{ onClick: () => void; label?: string }> = ({ onClick, label = "Retry" }) => (
  <OutlineButton onClick={onClick} icon={<IconRefreshGlyph size={13} strokeWidth={2} />}>
    {label}
  </OutlineButton>
);

/** 36px primary — the trigger actions. --accent on white Black 12.5px; disabled --surface-2/--muted. */
export const PrimaryButton: FC<{
  onClick?: () => void;
  icon?: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  full?: boolean;
}> = ({ onClick, icon, children, disabled, full }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={classNames(
      "inline-flex items-center justify-center gap-2 h-9 px-4 rounded-[10px] font-CircularStdBlack transition-colors",
      disabled ? "bg-surface-2 text-muted cursor-not-allowed" : "bg-accent text-white hover:bg-accent-dark",
      full && "w-full",
    )}
    style={{ fontSize: 12.5 }}
  >
    {icon}
    {children}
  </button>
);

/** "Checking every 5 seconds" — the visible contract of the poll. 5px --up dot + 11px --text-2. */
export const PollIndicator: FC<{ children?: ReactNode }> = ({ children = "Checking every 5 seconds" }) => (
  <span className="inline-flex items-center gap-1.5 text-text-2" style={{ fontSize: 11 }}>
    <span className="rounded-full bg-up animate-pulse motion-reduce:animate-none" style={{ width: 5, height: 5 }} />
    {children}
  </span>
);

const sweep = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
`;

/**
 * Indeterminate 4px sweep — never a percentage. The request emits no progress
 * events, and a fake bar that stalls at 80% is worse than none.
 */
export const ProgressSweep: FC = () => (
  <Box
    className="relative overflow-hidden rounded-full bg-track"
    sx={{ height: 4, width: "min(220px, 100%)", mx: "auto" }}
    role="progressbar"
    aria-label="Scoring in progress"
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: "33%",
        borderRadius: 999,
        background: "var(--accent)",
        animation: `${sweep} 1.6s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none", width: "100%", opacity: 0.5 },
      }}
    />
  </Box>
);

/**
 * An unbounded exception string gets a mono block clamped to two lines with
 * Copy — never inlined into a sentence, where a stack trace destroys the layout
 * and reads as a crash.
 */
export const MonoError: FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (insecure context, permissions); the text is still visible.
    }
  };
  return (
    <div className="flex items-start gap-2 rounded-lg bg-surface border border-border-token px-2.5 py-2 text-left">
      <code
        className="flex-1 min-w-0 text-text-2"
        style={{
          fontSize: 11,
          lineHeight: 1.45,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-all",
        }}
      >
        {text}
      </code>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 text-accent-text hover:underline"
        style={{ fontSize: 11 }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};
